<template>
  <!-- Affiche le contenu seulement si "ready" -->
  <b-card no-body v-if="ready" class="card-container">
    <!-- HEADER -->
    <template #header>
      <b-row class="mb-2 mt-2">
        <b-col md="6">
          <!-- Barre de recherche / filtres de base -->
          <b-input-group class="search-widget">
            <template #prepend>
              <b-input-group-text>
                <font-awesome-icon icon="search"></font-awesome-icon>
              </b-input-group-text>
            </template>
            <b-form-input
              type="search"
              :placeholder="$t('search_user')"
              v-model="filters.query"
            ></b-form-input>
            <template #append>
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  @click="showFilters = !showFilters"
                >
                  <font-awesome-icon icon="sliders-h"></font-awesome-icon>
                  {{ $t("advanced_search") }}
                </b-button>
              </b-input-group-append>
            </template>
          </b-input-group>

          <!-- Filtres avancés -->
          <div
            class="users-filters card p-3 bg-gradient-light mt-3"
            v-show="showFilters"
          >
            <b-row>
              <b-col>
                <!-- Sélecteur de company -->
                <b-form-select
                  v-model="filters.company_id"
                  class="mb-2 mr-sm-2 mb-sm-0"
                  :value="null"
                  :options="companies"
                >
                  <template #first>
                    <option :value="null">
                      {{ $t("select_company") }}
                    </option>
                  </template>
                </b-form-select>
              </b-col>
              <b-col>
                <!-- Checkbox group pour status -->
                <b-form-checkbox-group
                  v-model="filters.status"
                  :options="statusOptions"
                  stacked
                ></b-form-checkbox-group>
              </b-col>
            </b-row>
          </div>
        </b-col>

        <!-- Bouton "nouvel utilisateur" -->
        <b-col md="6" class="text-right" v-if="can('manage users')">
          <b-button :href="route('users.new')" variant="primary" class="btn-round">
            <font-awesome-icon :icon="['fas', 'plus']" />
            {{ $t("create_user") }}
          </b-button>
        </b-col>
      </b-row>
    </template>

    <!-- BODY -->
    <b-card-body>
      <!-- Tableau principal -->
      <b-table
        striped
        hover
        responsive
        id="users"
        ref="users"
        :fields="fields"
        :items="getItems"
        :current-page="pagination.currentPage"
        :sort-by="sorting.sortBy"
        :sort-desc="sorting.desc"
        :busy="loading"
        show-empty
        :empty-text="$t('no_user_found')"
      >
        <!-- Slot vide (aucun item) -->
        <template #empty="scope">
          <div class="lead text-center py-3">
            {{ scope.emptyText }}
          </div>
        </template>

        <!-- Overlay de chargement -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>{{ $t("loading") }}</strong>
          </div>
        </template>

        <!-- Colonne : username -->
        <template #cell(username)="data">
          <div class="d-sm-flex justify-content-sm-between">
            {{ data.item.first_name + " " + data.item.last_name }}
            <span
              class="badge badge-danger"
              v-if="!data.item.is_active"
            >
              {{ $t('user_status_inactive') }}
            </span>
          </div>
        </template>

        <!-- Colonne : company -->
        <template #cell(company)="data">
          <a :href="route('companies.edit', { company: data.item.company.id })">
            {{ data.item.company.name }}
          </a>
          <div class="text-capitalize">
            {{ data.item.company.type }}
          </div>
        </template>

        <!-- Colonne : role -->
        <template #cell(role)="data">
          <div class="text-capitalize">
            {{ data.item.role.name }}
          </div>
        </template>

        <!-- Colonne : created_at -->
        <template #cell(created_at)="data">
          <div class="text-capitalize">
            {{ formatDate(data.item.created_at) }}
          </div>
        </template>

        <!-- Colonne : actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button-toolbar key-nav justify style="flex-wrap: nowrap">
                <b-button
                  :href="`/users/${data.item.id}`"
                  variant="outline-primary"
                  class="mr-1"
                  size="sm"
                >
                  <font-awesome-icon :icon="['fas', 'pen']" />
                  {{ $t("edit") }}
                </b-button>

                <delete-button
                  v-if="can('manage users')"
                  @success="deleteItem(data.item)"
                  class="ml-2 actions-danger"
                  type="User"
                  :title="`Delete user`"
                ></delete-button>
            </b-button-toolbar>
          </div>
        </template>
      </b-table>

      <!-- Modales d'erreur / succès -->
      <b-modal
        id="modal-error"
        size="sm"
        title="Something went wrong :("
        hide-footer
      >
        <template>
          <div class="d-flex justify-content-between flex-column">
            <h6>{{ errorMessage }}</h6>
          </div>
        </template>
      </b-modal>

      <b-modal
        id="modal-success"
        size="sm"
        title="Success"
        hide-footer
      >
        <template>
          <div class="d-flex justify-content-between flex-column">
            <h6>{{ successMessage }}</h6>
          </div>
        </template>
      </b-modal>

      <!-- Pagination -->
      <b-pagination
        v-if="pagination.lastPage > 1"
        v-model="pagination.currentPage"
        :total-rows="pagination.total"
        :per-page="pagination.perPage"
        aria-controls="users"
      ></b-pagination>
    </b-card-body>

    <!-- FOOTER -->
    <b-card-footer>
      <div class="text-center" v-if="pagination.total > 1">
        {{
          $t("showing_x_results", {
            current: pagination.to - pagination.from + 1,
            total: pagination.total,
          })
        }}
        <span v-if="hasFilter">
          · {{ $t("results_filtered") }} ·
          <b-link @click="resetFilters">{{ $t("clear_all") }}</b-link>
        </span>
      </div>
    </b-card-footer>
  </b-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import axios from "axios";
import moment from "moment";
import deleteButton from "../Common/DeleteButton";

export default {
  name: "UserList",
  components: { deleteButton },
  props: {
    isAdmin: {
      type: Boolean,
      default: false
    },
    permissions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // Champs de la table
      fields: this.getFields(),
      // Provider pour <b-table>
      items: this.getItems,
      // Filtres
      filters: {
        query: null,
        company_id: null,
        status: []
      },
      // Options de statut
      statusOptions: [
        { text: this.$t("Active"), value: "1" },
        { text: this.$t("Inactive"), value: "0" }
      ],
      // Liste de companies pour le select
      companies: [],
      // Affichage des filtres avancés
      showFilters: false,
      // État du composant
      ready: false,
      errorMessage: "",
      successMessage: ""
    };
  },
  computed: {
    ...mapState({
      users: state => state.users.users,
      pagination: state => state.users.pagination,
      sorting: state => state.users.sorting,
      loading: state => state.users.loading,
      me: state => state.me.me
    }),
    hasFilter() {
      // Vérifie si un filtre est renseigné
      const isEmpty = Object.values(this.filters).every(
        x => x === null || x === "" || x.length === 0
      );
      return !isEmpty;
    }
  },
  watch: {
    // Surveille les filtres. On repasse la pagination à 1 et on recharge.
    filters: {
      handler: _.debounce(function() {
        this.updatePagination(1);
        this.reload();
      }, 500),
      deep: true
    }
  },
  mounted() {
    this.getMe().then(() => {
      this.ready = true;
      this.getCompanies();
    });
  },
  methods: {
    formatDate(date) {
      return moment(date).format("DD-MM-YYYY HH:mm:ss");
    },
    can(permissionName) {
      return this.permissions.some(
        permission => permission.name === permissionName
      );
    },
    showSuccess(data) {
      this.successMessage = data.message;
      this.$bvModal.show("modal-success");

      // On recharge la liste
      this.reload();

      setTimeout(() => this.$bvModal.hide("modal-success"), 2000);
    },
    showError(data) {
      this.errorMessage = data.message;
      this.$bvModal.show("modal-error");
    },
    deleteItem(item) {
      this.$store
        .dispatch("users/deleteUser", item.id)
        .then(() => {
          this.showSuccess({
            message: this.$t("user_deleted_successfully")
          });
        })
        .catch(error => {
          const errorMessage = error.response
            ? error.response.data.message
            : this.$t("unknown_error");
          this.showError({ message: errorMessage });
        });
    },
    /**
     * Provider pour <b-table>. ctx = contexte, cb = callback
     */
    getItems(ctx, cb) {
      // Mettre à jour page, tri, filtres
      this.updatePagination(ctx.currentPage);
      this.updateSorting({ sortBy: ctx.sortBy, desc: ctx.sortDesc });
      this.updateFilters(this.filters);

      this.getUsers().then(() => {
        cb(this.users);
      });
      return null;
    },
    /**
     * Récupère la liste des companies (pour le filtre)
     */
    getCompanies() {
      return axios
        .get(route("api.companies.list"), {
          params: { noPagination: true }
        })
        .then(r => {
          this.companies = r.data.data.map(c => ({
            value: c.id,
            text: c.name
          }));
        });
    },
    /**
     * Émet l'événement de rafraîchissement de la table
     */
    reload() {
      this.$root.$emit("bv::refresh::table", "users");
    },
    /**
     * Définition des colonnes du tableau
     */
    getFields() {
      return [
        {
          key: "username",
          sortable: false,
          label: this.$t("full_name"),
          class: "text-nowrap align-middle"
        },
        {
          key: "email",
          sortable: true,
          label: this.$t("email"),
          class: "text-nowrap align-middle"
        },
        {
          key: "company",
          sortable: false,
          label: this.$t("company"),
          class: "text-nowrap align-middle"
        },
        {
          key: "role",
          sortable: false,
          label: this.$t("role"),
          class: "text-nowrap align-middle"
        },
        {
          key: "created_at",
          sortable: true,
          label: this.$t("registration_date"),
          class: "text-nowrap align-middle"
        },
        {
          key: "actions",
          sortable: false,
          label: this.$t("actions"),
          class: "align-middle"
        }
      ];
    },
    /**
     * Réinitialise les filtres
     */
    resetFilters() {
      this.filters.query = null;
      this.filters.company_id = null;
      this.filters.status = [];
    },
    ...mapActions({
      getUsers: "users/getUsers",
      deleteUser: "users/deleteUser",
      updatePagination: "users/updatePagination",
      updateSorting: "users/updateSorting",
      updateFilters: "users/updateFilters",
      getMe: "me/getMe"
    })
  }
};
</script>

<style scoped>
.card-container {
  margin-bottom: 1rem;
}

.users-filters {
  border: 1px solid #ddd;
  border-radius: 4px;
}
</style>
